import { useState } from "react"
import {
  Box, Stack, StackProps,
  Chip,
  LinearProgress,
  List, ListItem, ListItemButton,
  Typography
} from "@mui/material"

import { BlockCaption } from "components/controls/blockCaption"

import { useKojinPreview } from "../../container"
import { SectionRowEditDialog } from "./dialog/sectionRowEditDialog"
import { EvaluationConsideration, EvaluationSectionRow } from "interface/search/evaluation"
import { ConsiderationEditDialog } from "./dialog/considerationEditDialog"

type KojinEvaluationPageProps = {
  container: ReturnType<typeof useKojinPreview>
} & StackProps
export const KojinEvaluationPage = ({
  container: {
    search,
    kojinCode,
    evaluationForm: form,
    evaluationResultSummary: summary,
    onEditEvaluation
  },
  ...others
}: KojinEvaluationPageProps) => {
  const [modal, setModal] = useState<{
    target: 'section' | 'consideration'
    row?: EvaluationSectionRow
    consideration?: EvaluationConsideration
  } | null>(null)

  return (
    <>
      <Stack
        direction='column'
        position="relative"
        overflow="auto"
        height="100%"
        padding={3}
        spacing={3}
        boxSizing="border-box"
        {...others}
      >
        <Stack direction="row" spacing={3} alignItems="center">
          <Stack direction="column" flexShrink={0} spacing={2} padding={2} alignItems="center" position="relative">
            <Typography variant="body2">≪ 総合評価 ≫</Typography>
            <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={1}>
              <Typography variant="h2" fontWeight="bold">{summary.totalScore}</Typography>
              <Typography variant="body2">/</Typography>
              <Typography variant="body2">{summary.maxScore}</Typography>
              <Typography variant="body2">点</Typography>
            </Stack>

            {summary.judgement && (
              <Chip color="primary" variant="filled" size="medium" sx={{ fontSize: "large", paddingX: 3 }} label={summary.judgement}/>
            )}
            
            <Stack direction="column" flexShrink={0} alignItems="flex-start" paddingY={2}>
              {form?.judgements?.map(({ label, minScore, maxScore }, i) => (
                <Stack key={`judgement-${i}`} direction="row" alignItems="center">
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="body2">：</Typography>
                  <Typography variant="body2">{minScore}</Typography>
                  <Typography variant="body2">～</Typography>
                  <Typography variant="body2">{maxScore}</Typography>
                  <Typography variant="body2">点</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Stack direction="column" flexGrow={1} spacing={1} boxSizing="border-box">
            {summary.sections?.map(({ sectionName, records: sectionValues, sectionTotal, maxScore, color }, i) => (
              <Box key={`section-box-${i}`}>
                <BlockCaption
                  caption={sectionName}
                  rightElement={(
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: '7rem', padding: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(sectionTotal * 100) / maxScore}
                          color={color}
                          sx={{
                            height: '0.5rem',
                            borderRadius: '0.25rem'
                          }}
                        />
                      </Box>
                      <Typography variant="body2">{sectionTotal}</Typography>
                      <Typography variant="body2">/</Typography>
                      <Typography variant="body2">{maxScore}</Typography>
                      <Typography variant="body2">点</Typography>
                    </Stack>
                  )}
                />
                <List>
                  {sectionValues.map((row, j) => (
                    <ListItem key={`section-${i}-field-${j}`} disableGutters disablePadding>
                      <ListItemButton disableRipple dense onClick={() => setModal({ target: 'section', row})}>
                        <Stack direction="row" alignItems="flex-start" spacing={1} width="100%">
                          <Typography variant="body2" flexShrink={0} width="11rem">{row.contentFieldId}</Typography>
                          <Typography variant="body2" flexGrow={1} whiteSpace="pre-wrap">{row.content ?? "-"}</Typography>
                          <Typography variant="body2" flexShrink={0} width="3rem" textAlign="center">{row.score ?? "-"}</Typography>
                        </Stack>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Stack>
        </Stack>
        
        <Box>
          <BlockCaption caption="追加考慮要素"/>
          <List>
            {summary.considerations?.map((consideration, i) => (
              <ListItem key={`consideration-${i}`} disableGutters disablePadding>
                <ListItemButton disableRipple dense onClick={() => setModal({ target: 'consideration', consideration })}>
                  <Stack direction="row" alignItems="flex-start" spacing={1} width="100%">
                    <Typography variant="body2" flexShrink={0} width="16rem">{consideration.fieldId}</Typography>
                    <Typography variant="body2" flexGrow={1} whiteSpace="pre-wrap">{consideration.content ?? "-"}</Typography>
                  </Stack>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Stack>

      {(modal?.target === 'section' && modal.row && search?.searchId && kojinCode && form?.scores) ? (
        <SectionRowEditDialog
          searchId={search.searchId}
          kojinCode={kojinCode}
          row={modal.row}
          scores={form.scores}
          onSubmit={() => {
            onEditEvaluation()
            setModal(null)
          }}
          onClose={() => setModal(null)}
        />
      ) : (modal?.target === 'consideration' && modal.consideration && search?.searchId && kojinCode)? (
        <ConsiderationEditDialog
          searchId={search.searchId}
          kojinCode={kojinCode}
          row={modal.consideration}
          onSubmit={() => {
            onEditEvaluation()
            setModal(null)
          }}
          onClose={() => setModal(null)}
        />
      ) : <></>}
    </>
  )
}
